import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CountdownPill } from "@/components/auction/CountdownPill";
import { AuctionStats } from "@/components/auction/AuctionStats";
import { StatusBadge } from "@/components/auction/StatusBadge";
import { SealedBidPanel } from "@/components/auction/SealedBidPanel";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Separator } from "@/components/ui/separator";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trophy, Flag, CreditCard, ShieldCheck, Truck, ScrollText, Loader2, Check } from "lucide-react";
import { VerifiedSellerBadge } from "@/components/VerifiedSellerBadge";

export default function AuctionDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [a, setA] = useState(null);
    const [active, setActive] = useState(0);
    const [paying, setPaying] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/auctions/${id}`);
            setA(data);
        } catch (e) { setA(false); }
    }, [id]);

    useEffect(() => {
        let active = true;
        const fetchOne = async () => {
            try {
                const { data } = await api.get(`/auctions/${id}`);
                if (active) setA(data);
            } catch (e) { if (active) setA(false); }
        };
        fetchOne();
        const t = setInterval(fetchOne, 10000);
        return () => { active = false; clearInterval(t); };
    }, [id]);

    const payNow = async () => {
        if (!user) { navigate("/login"); return; }
        setPaying(true);
        try {
            const { data } = await api.post("/payments/checkout", { auction_id: a.id, origin_url: window.location.origin });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start payment");
            setPaying(false);
        }
    };

    if (a === null) return <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6"><Skeleton className="h-[480px] w-full rounded-xl" /></div>;
    if (a === false) return <div className="mx-auto max-w-3xl px-4 py-20 text-center"><p className="font-display text-xl">Auction not found</p><Button asChild className="mt-4 rounded-[10px]"><Link to="/auctions">Back to browse</Link></Button></div>;

    const isWinner = user && a.winner_user_id === user.id;
    const images = a.images?.length ? a.images : ["https://images.unsplash.com/photo-1606293459339-aa5d34a7b0e1?w=800&q=80"];

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
            <div className="mb-4 text-sm text-muted-foreground">
                <Link to="/auctions" className="hover:text-foreground">Auctions</Link> / <Link to={`/auctions?category=${a.category}`} className="hover:text-foreground">{a.category_name}</Link>
            </div>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_0.8fr]">
                {/* Left */}
                <div>
                    <div className="overflow-hidden rounded-xl border border-border">
                        <AspectRatio ratio={4 / 3}>
                            <img src={images[active]} alt={a.title} className="h-full w-full object-cover" />
                        </AspectRatio>
                    </div>
                    {images.length > 1 && (
                        <div className="mt-3 flex gap-2">
                            {images.map((img, i) => (
                                <button key={img} onClick={() => setActive(i)} className={`h-16 w-20 overflow-hidden rounded-lg border ${i === active ? "border-primary" : "border-border"}`}>
                                    <img src={img} alt="" className="h-full w-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="mt-6 flex flex-wrap items-center gap-2">
                        <StatusBadge status={a.status} />
                        <CountdownPill endTime={a.end_time} status={a.status} />
                    </div>
                    <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight">{a.title}</h1>
                    <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground">
                        <span>Sold by {a.seller_id ? <Link to={`/seller/${a.seller_id}`} className="font-medium text-electric hover:underline" data-testid="auction-seller-link">{a.seller_name}</Link> : <span className="font-medium text-foreground">{a.seller_name}</span>}</span>
                        <VerifiedSellerBadge verified={a.seller_verified} />
                        <span>· {a.condition}</span>
                    </p>

                    <Tabs defaultValue="details" className="mt-6">
                        <TabsList>
                            <TabsTrigger value="details" data-testid="tab-details"><ScrollText className="mr-1.5 h-4 w-4" />Details</TabsTrigger>
                            <TabsTrigger value="shipping" data-testid="tab-shipping"><Truck className="mr-1.5 h-4 w-4" />Shipping</TabsTrigger>
                            <TabsTrigger value="rules" data-testid="tab-rules"><ShieldCheck className="mr-1.5 h-4 w-4" />Rules</TabsTrigger>
                        </TabsList>
                        <TabsContent value="details" className="mt-4 space-y-5">
                            <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{a.description || "No description provided."}</p>
                            {a.highlights?.length > 0 && (
                                <div>
                                    <h3 className="mb-2 font-display text-sm font-semibold">Highlights</h3>
                                    <ul className="space-y-1.5">
                                        {a.highlights.map((h) => (
                                            <li key={h} className="flex items-start gap-2 text-sm text-muted-foreground">
                                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />{h}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {a.specifics?.length > 0 && (
                                <div>
                                    <h3 className="mb-2 font-display text-sm font-semibold">Item specifics</h3>
                                    <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2" data-testid="auction-specifics">
                                        {a.specifics.map(([label, value]) => (
                                            <div key={label} className="flex items-center justify-between gap-3 bg-card px-3 py-2">
                                                <dt className="text-xs text-muted-foreground">{label}</dt>
                                                <dd className="text-sm font-medium">{value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            )}
                        </TabsContent>
                        <TabsContent value="shipping" className="mt-4 text-sm leading-relaxed text-muted-foreground">{a.shipping}</TabsContent>
                        <TabsContent value="rules" className="mt-4 text-sm leading-relaxed text-muted-foreground">
                            <ul className="list-disc space-y-1 pl-5">
                                <li>You may submit exactly one sealed bid for this item.</li>
                                <li>Bids are private and never revealed to other bidders.</li>
                                <li>The auction closes when the timer ends or when {a.max_bidders} unique bidders are reached.</li>
                                <li>The highest sealed bid wins; ties are broken by the earliest bid.</li>
                            </ul>
                        </TabsContent>
                    </Tabs>
                </div>

                {/* Right */}
                <div className="space-y-4">
                    <div className="rounded-xl border border-border bg-card p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">Starting price</p>
                                <p className="font-display text-2xl font-bold tabular-nums text-brass">{money(a.starting_price)}</p>
                            </div>
                            <StatusBadge status={a.status} />
                        </div>
                        <Separator className="my-4" />
                        <AuctionStats endTime={a.end_time} status={a.status} count={a.unique_bidder_count} max={a.max_bidders} />
                    </div>

                    {a.status !== "OPEN" && a.winner_user_id ? (
                        <WinnerPanel a={a} isWinner={isWinner} paying={paying} onPay={payNow} />
                    ) : a.status !== "OPEN" ? (
                        <div className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">
                            This auction closed with no qualifying bids.
                        </div>
                    ) : (
                        <SealedBidPanel auction={a} onBidPlaced={load} />
                    )}

                    <ReportDialog targetType="listing" targetId={a.id} />
                </div>
            </div>
        </div>
    );
}

function WinnerPanel({ a, isWinner, paying, onPay }) {
    return (
        <div className="rounded-xl border border-brass/40 bg-brass/10 p-5">
            <div className="flex items-center gap-2 text-brass-foreground">
                <Trophy className="h-5 w-5" />
                <h3 className="font-display font-semibold">Auction closed — winner revealed</h3>
            </div>
            <p className="mt-2 text-sm">Winning bid: <span className="font-display font-semibold tabular-nums">{money(a.winning_amount)}</span> by {a.winner_name}{isWinner ? " (you)" : ""}.</p>
            {isWinner && !a.paid && (
                <Button data-testid="pay-now-button" className="mt-4 w-full rounded-[10px]" onClick={onPay} disabled={paying}>
                    {paying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                    Pay {money(a.winning_amount)} now
                </Button>
            )}
            {isWinner && a.paid && <p className="mt-3 inline-flex items-center gap-1 text-sm text-success"><ShieldCheck className="h-4 w-4" /> Payment complete</p>}
        </div>
    );
}

function ReportDialog({ targetType, targetId }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [details, setDetails] = useState("");
    const submit = async () => {
        try {
            await api.post("/reports", { target_type: targetType, target_id: targetId, reason, details });
            toast.success("Report submitted to Trust & Safety");
            setOpen(false); setReason(""); setDetails("");
        } catch (e) { toast.error(e?.response?.data?.detail || "Please sign in to report"); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full rounded-[10px] text-muted-foreground" data-testid="report-listing-button"><Flag className="mr-1.5 h-4 w-4" />Report this listing</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle>Report listing</DialogTitle></DialogHeader>
                {!user ? <p className="text-sm text-muted-foreground">Please sign in to submit a report.</p> : (
                    <div className="space-y-3">
                        <Input placeholder="Reason (e.g. counterfeit, prohibited item)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="report-reason-input" />
                        <Textarea placeholder="Additional details (optional)" value={details} onChange={(e) => setDetails(e.target.value)} />
                    </div>
                )}
                <DialogFooter><Button onClick={submit} disabled={!user || !reason} className="rounded-[10px]" data-testid="report-submit-button">Submit report</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
